import { useCallback, useEffect, useMemo, useState } from "react";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { fetchAuditLog } from "../api.js";
import { useImpersonation } from "../hooks/useImpersonation.jsx";
import { formatAuditValue, formatRole, getActorDisplay, getTargetSummary, humanizeAuditAction, humanizeAuditKey } from "../lib/auditFormat.js";

const SENSITIVE_KEY = /(password|passwd|secret|token|cookie|authorization|credential|private.?key)/i;

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function formatName(session) {
  return [session?.firstName, session?.lastName].filter(Boolean).join(" ") || session?.email || (session?.userId ? `User ${session.userId}` : "Unknown");
}

function DetailItem({ label, value, mono = false }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="audit-detail-item">
      <dt>{label}</dt>
      <dd className={mono ? "audit-mono" : ""}>{String(value)}</dd>
    </div>
  );
}

function MetadataDetails({ metadata }) {
  const [showRaw, setShowRaw] = useState(false);
  const [expandedArrays, setExpandedArrays] = useState({});
  if (metadata === null || metadata === undefined) return <p className="audit-empty-detail">No metadata was recorded.</p>;
  const structured = typeof metadata === "object" && !Array.isArray(metadata);
  const entries = structured ? Object.entries(metadata).filter(([, value]) => value !== null && value !== undefined && value !== "") : [];
  const containsSensitiveKeys = entries.some(([key]) => SENSITIVE_KEY.test(key));
  return (
    <>
      {structured && entries.length > 0 ? (
        <dl className="audit-metadata-list">
          {entries.map(([key, value]) => {
            const sensitive = SENSITIVE_KEY.test(key);
            const isArray = Array.isArray(value);
            const isObject = value && typeof value === "object" && !isArray;
            return (
              <div className="audit-detail-item" key={key}>
                <dt>{humanizeAuditKey(key)}</dt>
                <dd>
                  {sensitive ? (
                    <span className="audit-sensitive">Sensitive value hidden in summary</span>
                  ) : isArray ? (
                    <>
                      <button type="button" className="audit-inline-button" onClick={() => setExpandedArrays((current) => ({ ...current, [key]: !current[key] }))}>
                        {value.length} {value.length === 1 ? "item" : "items"} · {expandedArrays[key] ? "Hide" : "View"}
                      </button>
                      {expandedArrays[key] && <pre className="audit-json audit-json--nested">{JSON.stringify(value, null, 2)}</pre>}
                    </>
                  ) : isObject ? (
                    <pre className="audit-json audit-json--nested">{JSON.stringify(value, null, 2)}</pre>
                  ) : (
                    formatAuditValue(value)
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : !structured ? (
        <p className="audit-metadata-value">{String(metadata)}</p>
      ) : (
        <p className="audit-empty-detail">No populated metadata fields were recorded.</p>
      )}
      <button type="button" className="audit-raw-toggle" onClick={() => setShowRaw((value) => !value)} aria-expanded={showRaw}>
        {showRaw ? "Hide raw metadata" : "Show raw metadata"}
      </button>
      {containsSensitiveKeys && (
        <p className="audit-sensitive-note">Potentially sensitive metadata keys detected. Review raw data only when required for an authorized audit or investigation.</p>
      )}
      {showRaw && <pre className="audit-json">{typeof metadata === "string" ? metadata : JSON.stringify(metadata, null, 2)}</pre>}
    </>
  );
}

function AuditDetails({ entry }) {
  return (
    <div className="audit-details" id={`audit-details-${entry.id}`}>
      <section>
        <h3>Event</h3>
        <dl>
          <DetailItem label="Action" value={humanizeAuditAction(entry.action)} />
          <DetailItem label="Raw action" value={entry.action} mono />
          <DetailItem label="Timestamp" value={formatDate(entry.createdAt)} />
          <DetailItem label="Audit ID" value={entry.id} mono />
        </dl>
      </section>
      <section>
        <h3>Actor</h3>
        <dl>
          <DetailItem label="Name" value={[entry.actorFirstName, entry.actorLastName].filter(Boolean).join(" ")} />
          <DetailItem label="Email" value={entry.actorEmail} />
          <DetailItem label="User ID" value={entry.actorUserId} mono />
          <DetailItem label="Role" value={formatRole(entry.actorRole)} />
          {!entry.actorUserId && <DetailItem label="Source" value="System-generated or API-token action" />}
        </dl>
      </section>
      <section>
        <h3>Tenant</h3>
        <dl>
          <DetailItem label="Company" value={entry.tenantName} />
          <DetailItem label="Tenant ID" value={entry.tenantId} mono />
          <DetailItem label="Slug" value={entry.tenantSlug} mono />
        </dl>
      </section>
      <section>
        <h3>Target</h3>
        <dl>
          <DetailItem label="Summary" value={getTargetSummary(entry)} />
          <DetailItem label="Type" value={entry.targetType} mono />
          <DetailItem label="ID" value={entry.targetId} mono />
        </dl>
      </section>
      <section>
        <h3>Request / device</h3>
        <dl>
          <DetailItem label="IP address" value={entry.ip} mono />
          <DetailItem label="User agent" value={entry.userAgent} />
          <DetailItem label="Device ID" value={entry.metadata?.deviceId || entry.metadata?.device_id} mono />
          <DetailItem label="Session ID" value={entry.metadata?.sessionId || entry.metadata?.session_id} mono />
          <DetailItem label="Request ID" value={entry.metadata?.requestId || entry.metadata?.request_id} mono />
        </dl>
      </section>
      <section className="audit-details__metadata">
        <h3>Metadata</h3>
        <MetadataDetails metadata={entry.metadata} />
      </section>
    </div>
  );
}

export default function OpsAuditLog() {
  const { impersonating, session, startImpersonation, stopImpersonation, updating } = useImpersonation();
  const [loading, setLoading] = useState(false),
    [error, setError] = useState(null),
    [entries, setEntries] = useState([]);
  const [limit, setLimit] = useState(100),
    [tenantFilter, setTenantFilter] = useState(""),
    [refreshToken, setRefreshToken] = useState(0);
  const [startTenantId, setStartTenantId] = useState(""),
    [startUserId, setStartUserId] = useState(""),
    [starting, setStarting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      const parsedLimit = Number(limit),
        parsedTenant = Number(tenantFilter);
      if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
        params.page = 1;
        params.pageSize = Math.min(parsedLimit, 100);
      }
      if (Number.isFinite(parsedTenant) && parsedTenant > 0) params.tenantId = parsedTenant;
      const data = await fetchAuditLog(params);
      setEntries(Array.isArray(data?.items) ? data.items : Array.isArray(data?.entries) ? data.entries : []);
    } catch (err) {
      setError(err?.message || "Failed to load audit log");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [limit, tenantFilter]);
  useEffect(() => {
    loadAudit().catch(() => {});
  }, [loadAudit, refreshToken]);
  const handleRefresh = (event) => {
    event.preventDefault();
    setExpandedId(null);
    setRefreshToken((value) => value + 1);
  };
  const handleStart = async (event) => {
    event.preventDefault();
    const tenantId = Number(startTenantId),
      userId = Number(startUserId);
    if (!Number.isFinite(tenantId) || tenantId <= 0) return setError("Enter a valid tenant ID to impersonate.");
    if (!Number.isFinite(userId) || userId <= 0) return setError("Enter a valid user ID to impersonate.");
    setError(null);
    setStarting(true);
    try {
      await startImpersonation(tenantId, userId);
    } catch (err) {
      setError(err?.message || "Failed to start impersonation");
    } finally {
      setStarting(false);
    }
  };
  const handleStop = async () => {
    setError(null);
    try {
      await stopImpersonation();
    } catch (err) {
      setError(err?.message || "Failed to stop impersonation");
    }
  };
  const currentSession = useMemo(() => (impersonating && session ? session : null), [impersonating, session]);

  return (
    <ErrorBoundary componentName="OpsAuditLogPage">
      <main className="audit-page">
        <header className="audit-header">
          <div>
            <h1>Audit Log</h1>
            <p>Review operator and system activity without losing technical context.</p>
          </div>
        </header>
        <div className="audit-container">
          <details className="audit-card audit-impersonation" open={Boolean(currentSession)}>
            <summary>Impersonation controls</summary>
            <div className="audit-impersonation__body">
              {currentSession ? (
                <>
                  <p className="audit-warning">
                    Currently impersonating <strong>{formatName(currentSession)}</strong>
                    {currentSession.email ? ` (${currentSession.email})` : ""}
                    {currentSession.tenantName || currentSession.tenantSlug ? ` — ${currentSession.tenantName || currentSession.tenantSlug}` : ""}
                  </p>
                  <button type="button" className="audit-button audit-button--danger" onClick={handleStop} disabled={updating}>
                    {updating ? "Stopping…" : "Stop impersonation"}
                  </button>
                </>
              ) : (
                <form className="audit-impersonation-form" onSubmit={handleStart}>
                  <label>
                    Tenant ID
                    <input type="number" min="1" required value={startTenantId} onChange={(event) => setStartTenantId(event.target.value)} />
                  </label>
                  <label>
                    User ID
                    <input type="number" min="1" required value={startUserId} onChange={(event) => setStartUserId(event.target.value)} />
                  </label>
                  <button className="audit-button" disabled={starting}>
                    {starting ? "Starting…" : "Start impersonation"}
                  </button>
                </form>
              )}
            </div>
          </details>
          <form className="audit-card audit-filters" onSubmit={handleRefresh}>
            <div>
              <h2>Activity</h2>
              <p>Newest events first. Open one event to inspect every recorded field.</p>
            </div>
            <label>
              Tenant ID
              <input type="number" min="1" value={tenantFilter} onChange={(event) => setTenantFilter(event.target.value)} placeholder="All tenants" />
            </label>
            <label>
              Events shown
              <select value={limit} onChange={(event) => setLimit(event.target.value)}>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </label>
            <button className="audit-button" disabled={loading}>
              {loading ? "Loading…" : "Apply filters"}
            </button>
          </form>
          {error && (
            <div className="audit-error" role="alert">
              {error}
            </div>
          )}
          <section className="audit-card audit-events" aria-busy={loading}>
            <div className="audit-events__heading">
              <h2>Events</h2>
              <span>{entries.length ? `${entries.length} shown` : ""}</span>
            </div>
            {loading ? (
              <div className="audit-state">
                <span className="audit-spinner" />
                Loading audit log…
              </div>
            ) : entries.length === 0 ? (
              <div className="audit-state">No audit events found.</div>
            ) : (
              <div className="audit-list" role="table" aria-label="Audit events">
                <div className="audit-row audit-row--header" role="row">
                  <span>When</span>
                  <span>Activity</span>
                  <span>Actor</span>
                  <span>Tenant</span>
                  <span className="audit-row__control">Details</span>
                </div>
                {entries.map((entry) => {
                  const expanded = expandedId === entry.id;
                  return (
                    <div className={`audit-event${expanded ? " audit-event--expanded" : ""}`} key={entry.id}>
                      <button
                        type="button"
                        className="audit-row audit-row--event"
                        onClick={() => setExpandedId(expanded ? null : entry.id)}
                        aria-expanded={expanded}
                        aria-controls={`audit-details-${entry.id}`}>
                        <span className="audit-when">{formatDate(entry.createdAt)}</span>
                        <span className="audit-activity">
                          <strong>{humanizeAuditAction(entry.action)}</strong>
                          <small>{getTargetSummary(entry)}</small>
                        </span>
                        <span className="audit-actor">
                          <strong>{getActorDisplay(entry)}</strong>
                          <small>{formatRole(entry.actorRole) || (entry.actorUserId ? "Identity unavailable" : "System action")}</small>
                        </span>
                        <span className="audit-tenant">
                          <strong>{entry.tenantName || "No tenant"}</strong>
                          <small>{entry.tenantName ? "Tenant" : "Global event"}</small>
                        </span>
                        <span className="audit-row__control">
                          {expanded ? "Hide" : "View"}
                          <i aria-hidden="true">⌄</i>
                        </span>
                      </button>
                      {expanded && <AuditDetails entry={entry} />}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>
    </ErrorBoundary>
  );
}
